import api from './api';

export interface Customer {
  _id: string;
  partyType: 'buyer' | 'vendor';
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  postcode: string;
  notes: string;
  status: 'active' | 'inactive' | 'prospect' | 'client';
  source: string;
  assignedTo?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  tags: string[];
  
  // Property seeking preferences
  budgetMin?: number;
  budgetMax?: number;
  preferredAreas: string[];
  propertyTypes: string[];
  bedrooms?: number;
  bathrooms?: number;
  parkingSpaces?: number;
  mustHaveFeatures: string[];
  niceToHaveFeatures: string[];
  
  // Timeline and urgency
  timeline?: 'immediate' | '1-3_months' | '3-6_months' | '6-12_months' | 'flexible';
  urgency?: 'high' | 'medium' | 'low';
  
  // Previous property experience
  previouslyOwned?: boolean;
  previousProperties: string[];
  sellingReason?: string;
  
  // Financial information
  preApproved?: boolean;
  preApprovalAmount?: number;
  depositAmount?: number;
  lender?: string;
  
  // Additional contact info
  alternatePhone?: string;
  preferredContactMethod?: 'email' | 'phone' | 'sms';
  bestContactTime?: 'morning' | 'afternoon' | 'evening' | 'anytime';
  
  // Family and lifestyle
  familySize?: number;
  children?: boolean;
  pets?: boolean;
  workFromHome?: boolean;
  
  // Communication history
  lastContactDate?: string;
  nextFollowUpDate?: string;
  communicationNotes?: string;
  purchases?: Array<{
    propertyId: string;
    purchasedAt?: string;
    price?: number;
    notes?: string;
  }>;
  ownedProperties?: Array<{
    _id: string;
    address?: { street?: string; city?: string; state?: string; formattedAddress?: string };
    status?: string;
  }>;
  
  createdAt: string;
  updatedAt: string;
}

export interface CustomerStats {
  total: number;
  prospect: number;
  active: number;
  client: number;
  inactive: number;
}

export interface CreateCustomerData {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  postcode?: string;
  notes?: string;
  status?: 'active' | 'inactive' | 'prospect' | 'client';
  source?: string;
  assignedTo?: string;
  tags?: string[];
  
  // Property seeking preferences
  budgetMin?: number;
  budgetMax?: number;
  preferredAreas?: string[];
  propertyTypes?: string[];
  bedrooms?: number;
  bathrooms?: number;
  parkingSpaces?: number;
  mustHaveFeatures?: string[];
  niceToHaveFeatures?: string[];
  
  // Timeline and urgency
  timeline?: 'immediate' | '1-3_months' | '3-6_months' | '6-12_months' | 'flexible';
  urgency?: 'high' | 'medium' | 'low';
  
  // Previous property experience
  previouslyOwned?: boolean;
  previousProperties?: string[];
  sellingReason?: string;
  
  // Financial information
  preApproved?: boolean;
  preApprovalAmount?: number;
  depositAmount?: number;
  lender?: string;
  
  // Additional contact info
  alternatePhone?: string;
  preferredContactMethod?: 'email' | 'phone' | 'sms';
  bestContactTime?: 'morning' | 'afternoon' | 'evening' | 'anytime';
  
  // Family and lifestyle
  familySize?: number;
  children?: boolean;
  pets?: boolean;
  workFromHome?: boolean;
  
  // Communication history
  lastContactDate?: string;
  nextFollowUpDate?: string;
  communicationNotes?: string;
}

export interface UpdateCustomerData extends Partial<CreateCustomerData> {}

// Get all customers for an agency
export const getCustomers = async (
  agencySlug: string,
  filters?: {
    status?: string;
    search?: string;
    assignedTo?: string;
    partyType?: 'buyer' | 'vendor';
  }
): Promise<Customer[]> => {
  const params = new URLSearchParams();
  if (filters?.status) params.append('status', filters.status);
  if (filters?.search) params.append('search', filters.search);
  if (filters?.assignedTo) params.append('assignedTo', filters.assignedTo);
  if (filters?.partyType) params.append('partyType', filters.partyType);

  const response = await api.get(`/customers/${agencySlug}?${params.toString()}`);
  return response.data;
};

// Get a single customer
export const getCustomer = async (agencySlug: string, customerId: string): Promise<Customer> => {
  const response = await api.get(`/customers/${agencySlug}/${customerId}`);
  return response.data;
};

// Create a new customer
export const createCustomer = async (agencySlug: string, customerData: CreateCustomerData): Promise<Customer> => {
  const response = await api.post(`/customers/${agencySlug}`, customerData);
  return response.data;
};

// Update a customer
export const updateCustomer = async (
  agencySlug: string,
  customerId: string,
  customerData: UpdateCustomerData
): Promise<Customer> => {
  const response = await api.put(`/customers/${agencySlug}/${customerId}`, customerData);
  return response.data;
};

// Delete a customer
export const deleteCustomer = async (agencySlug: string, customerId: string): Promise<void> => {
  await api.delete(`/customers/${agencySlug}/${customerId}`);
};

// Get customer statistics
export const getCustomerStats = async (agencySlug: string, partyType?: 'buyer' | 'vendor'): Promise<CustomerStats> => {
  const params = new URLSearchParams();
  if (partyType) params.append('partyType', partyType);
  const qs = params.toString();
  const response = await api.get(`/customers/${agencySlug}/stats${qs ? `?${qs}` : ''}`);
  return response.data;
}; 